import { useEffect, useRef, useState } from 'react';
import type * as Leaflet from 'leaflet';

// Reserve the map's space in HTML, then load the map library when it approaches the viewport.
export function useServiceMap() {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Leaflet.Map | null>(null);
  const markersRef = useRef<Leaflet.LayerGroup | null>(null);
  const leafletRef = useRef<typeof Leaflet | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;
    let cancelled = false;
    const observer = new IntersectionObserver(async entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      try {
        const [L] = await Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')]);
        if (cancelled) return;
        const map = L.map(container, { center: [33.15, -96.5], zoom: 9, scrollWheelZoom: false, zoomControl: true });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);
        container.setAttribute('role', 'region');
        container.setAttribute('aria-label', 'Interactive map of our North Texas service areas');
        leafletRef.current = L;
        markersRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;
        setMapReady(true);
      } catch (error) {
        console.error('Unable to load the service area map:', error);
        if (!cancelled) container.textContent = 'The map is unavailable. Please use the city directory to find your service area.';
      }
    }, { rootMargin: '300px' });
    observer.observe(container);
    return () => {
      cancelled = true;
      observer.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
      markersRef.current = null;
      leafletRef.current = null;
    };
  }, []);
  return { mapContainerRef, mapRef, markersRef, leafletRef, mapReady };
}
